/**
 * HandoffViewer — public parent hand-off PDF viewer.
 *
 * Lives at `/handoff/:token_id` (Cloudflare Pages _redirects keeps
 * the URL stable so the SPA can match it). No auth required — the
 * token IS the auth. Resolved via the
 * `api-resolve-parent-handoff-token` Edge Function which is the
 * ONLY public Edge Function in the platform (verify_jwt=false).
 *
 * States:
 *   - exchanging: token in flight
 *   - resolved: PDF bytes in hand, triggers browser download.
 *     `data.consumed === true` is informational only (the SLP set
 *     a FERPA-strict max_views cap and the parent hit it) — the
 *     PDF still renders, with a soft "view limit reached" notice
 *     above it. This is NOT a fatal error.
 *   - error: token missing/expired/revoked/not_found (parent sees
 *     a friendly message; the audit log still records the attempt
 *     server-side via the Edge Function).
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { shouldShowConsumedNotice } from '../lib/handoff/consumedNotice';

interface ResolveSuccess {
  pdf_base64: string;
  filename: string;
  student_first_name: string;
  /** true when max_views cap was hit (rare; FERPA-strict opt-in). */
  consumed: boolean;
  /** null until first view (per docs/research/share-link-ttl-ferpa.md). */
  expires_at: string | null;
}

export type { ResolveSuccess };

type ResolveError =
  | 'not_found'
  | 'expired'
  | 'revoked'
  // NOTE: `consumed` was removed from this union. The server still
  // returns 410 + `error: "consumed"` when an SLP has set a
  // FERPA-strict max_views cap and the parent hit it, but the
  // client now treats that as the generic `unknown` error path.
  // Normal multi-use within TTL never produces `error: "consumed"`
  // — it returns 200 with `consumed: false` in the payload, and
  // that's the resolved-state branch below. See
  // docs/LAUNCH_BLOCKED.md §A.3 for the FERPA rationale.
  | 'missing_token'
  | 'network'
  | 'unknown';

type Status =
  | { kind: 'exchanging' }
  | { kind: 'resolved'; data: ResolveSuccess }
  | { kind: 'error'; code: ResolveError };

const HUMAN_ERROR: Record<ResolveError, { title: string; body: string }> = {
  not_found: {
    title: 'Link not found',
    body: 'This hand-off link doesn\u2019t exist. Ask your child\u2019s SLP to send a new one.',
  },
  expired: {
    title: 'Link expired',
    body: 'Hand-off links are valid for 14 days after the parent first opens them. Ask your child\u2019s SLP to send a new one.',
  },
  revoked: {
    title: 'Link was revoked',
    body: 'Your child\u2019s SLP ended this link. Reach out to them for a new one.',
  },
  // `consumed` removed — multi-use within TTL is the default, and a
  // FERPA-strict max_views cap (the only path that surfaces this
  // signal server-side) now degrades to the `unknown` error below
  // rather than blocking the parent. The `data.consumed` flag is
  // surfaced as a soft notice in the resolved state instead — see
  // the `shouldShowConsumedNotice` helper below.
  missing_token: {
    title: 'Invalid link',
    body: 'The URL looks incomplete. Make sure you copied the whole link from the email.',
  },
  network: {
    title: 'Could not reach the server',
    body: 'Check your internet connection and try again. The link is still valid.',
  },
  unknown: {
    title: 'Something went wrong',
    body: 'Try again in a moment. If the problem persists, reach out to your child\u2019s SLP.',
  },
};

interface Props {
  tokenId: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

/**
 * Decode base64 → Uint8Array, mirroring SessionNotesScreen's
 * base64ToUint8 helper. We duplicate it here so the HandoffViewer
 * can be tested in isolation.
 */
function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Render the resolved-state branch of the HandoffViewer given a
 * pre-resolved `ResolveSuccess` payload. Extracted from the
 * parent `HandoffViewer` component so tests can render the
 * resolved branch directly (the parent's `useEffect` fetch only
 * fires client-side; SSR can't drive it). This is the same JSX
 * the parent renders when `status.kind === 'resolved'`.
 */
export function ResolvedHandoff({
  data,
}: {
  data: ResolveSuccess;
}): ReactElement {
  const dataUrl = `data:application/pdf;base64,${data.pdf_base64}`;
  const studentGreeting = `Here's ${data.student_first_name}'s hand-off.`;
  const showConsumedNotice = shouldShowConsumedNotice(data);

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Hand-off</h1>
          <p className="tagline">{studentGreeting}</p>
        </header>
      </div>
      {showConsumedNotice ? (
        <p className="auth-confirm" role="status">
          This link has reached its view limit. Your child&rsquo;s SLP set
          a maximum number of views for added privacy; the PDF below is
          still available, but future visits will need a new link from
          the SLP.
        </p>
      ) : null}
      <p>
        Your browser opened this link to view the hand-off. You can
        read it below, save it, or print it for your records.
      </p>
      <object
        data={dataUrl}
        type="application/pdf"
        width="100%"
        height="600"
        aria-label={`Parent hand-off PDF for ${data.student_first_name}`}
      >
        <p>
          Your browser couldn&rsquo;t display the PDF inline.{' '}
          <a href={dataUrl} download={data.filename}>
            Download {data.filename}
          </a>{' '}
          instead.
        </p>
      </object>
      <a href={dataUrl} download={data.filename} className="snap-button">
        Download PDF
      </a>
      <p className="tagline">
        For privacy, this link expires 14 days after first view
        (FERPA best-practice; matches DocuSign envelope TTL).
        Multi-use within that window so you can re-open on any
        device or share with a co-parent. Reach out to your
        child&rsquo;s SLP for a new link if the window has passed.
      </p>
    </main>
  );
}

export function HandoffViewer({
  tokenId,
  supabaseUrl,
  supabaseAnonKey,
}: Props): ReactElement {
  const [status, setStatus] = useState<Status>({ kind: 'exchanging' });

  useEffect(() => {
    if (!tokenId) {
      setStatus({ kind: 'error', code: 'missing_token' });
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        // Resolve endpoint uses the Supabase Functions URL with the
        // token as a query-string param (Cloudflare Pages rewrites
        // /handoff/:token_id to the SPA, the SPA calls the function
        // directly). We pass the anon key as apikey so the
        // Edge Function can run with verify_jwt=false.
        const url =
          `${supabaseUrl}/functions/v1/api-resolve-parent-handoff-token?token=` +
          encodeURIComponent(tokenId);
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            apikey: supabaseAnonKey,
            authorization: `Bearer ${supabaseAnonKey}`,
          },
        });

        if (cancelled) return;

        if (!res.ok) {
          // Map HTTP status to our friendly error code. The server
          // still returns 410 + `error: "consumed"` for the FERPA-
          // strict max_views cap case, but the client no longer has
          // a fatal error path for it — it falls through to
          // `unknown` and surfaces the generic "try again" copy.
          const errBody = (await res.json().catch(() => ({ error: 'unknown' }))) as {
            error?: string;
          };
          const code = (
            ['not_found', 'expired', 'revoked'] as ResolveError[]
          ).includes(errBody.error as ResolveError)
            ? (errBody.error as ResolveError)
            : 'unknown';
          setStatus({ kind: 'error', code });
          return;
        }

        const data = (await res.json()) as ResolveSuccess;
        setStatus({ kind: 'resolved', data });
      } catch {
        if (!cancelled) setStatus({ kind: 'error', code: 'network' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenId, supabaseUrl, supabaseAnonKey]);

  if (status.kind === 'exchanging') {
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>Loading your hand-off…</h1>
            <p className="tagline">Verifying the link.</p>
          </header>
        </div>
        <p className="tagline">This usually takes a second.</p>
      </main>
    );
  }

  if (status.kind === 'error') {
    const human = HUMAN_ERROR[status.code];
    return (
      <main className="screen handoff">
        <div className="auth-hero">
          <header className="screen-header">
            <h1>{human.title}</h1>
          </header>
        </div>
        <section className="auth-confirm">
          <p>{human.body}</p>
          <p className="tagline">
            Need help? Reach out to your child&rsquo;s SLP directly.
          </p>
        </section>
      </main>
    );
  }

  // Resolved: render the PDF inline + offer a download button.
  // The auto-download triggers on first paint; the on-page
  // rendering is the second-click affordance. The full render is
  // extracted to <ResolvedHandoff> for testability (the parent
  // component's `useEffect` fetch only fires client-side, so SSR
  // can't drive the resolved branch directly).
  void base64ToUint8(status.data.pdf_base64); // exercise the helper so tree-shaking doesn't drop it
  return <ResolvedHandoff data={status.data} />;
}